import React, { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Upload, X, UserPlus, FileSpreadsheet, ArrowRight, Search, Loader2, Building2, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

function parseCSV(text) {
  const rows = [];
  let row = [], cur = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === "," || ch === ";") { row.push(cur); cur = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cur); rows.push(row); row = []; cur = "";
    } else cur += ch;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

const FIELDS = [
  { k: "name", l: "Nom" },
  { k: "email", l: "Email" },
  { k: "phone", l: "Téléphone" },
  { k: "company_name", l: "Société" },
  { k: "group", l: "Groupe" },
];

export function ClientCreateImport({ onDone }) {
  const { managed, selectedId } = useCompany();
  const defCompany = (selectedId && selectedId !== "all") ? selectedId : (managed[0]?.id || "");
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ company_id: defCompany, name: "", email: "", phone: "", title: "", company_name: "", group: "", siret: "", siren: "", address: "", naf: "", en_creation: false });
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState(false);
  const [lookupQ, setLookupQ] = useState("");
  const [looking, setLooking] = useState(false);
  const [results, setResults] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [impCompany, setImpCompany] = useState(defCompany);

  useEffect(() => {
    if (createOpen) api.get("/contacts/groups").then(({ data }) => setGroups(data || [])).catch(() => setGroups([]));
  }, [createOpen]);

  const openCreate = () => {
    setForm({ company_id: defCompany, name: "", email: "", phone: "", title: "", company_name: "", group: "", siret: "", siren: "", address: "", naf: "", en_creation: false });
    setLookupQ(""); setResults([]); setNewGroup(false); setCreateOpen(true);
  };
  const openImport = () => { setHeaders([]); setDataRows([]); setMapping({}); setImpCompany(defCompany); setImportOpen(true); };

  const doLookup = async () => {
    if (lookupQ.trim().length < 3) { toast.error("Saisissez au moins 3 caractères (SIRET ou nom)"); return; }
    setLooking(true); setResults([]);
    try {
      const { data } = await api.get(`/company-lookup?q=${encodeURIComponent(lookupQ.trim())}`);
      if (!data.results?.length) toast.error("Aucune entreprise trouvée");
      setResults(data.results || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLooking(false); }
  };
  const pickCompany = (r) => {
    setForm((f) => ({ ...f, company_name: r.company_name, siret: r.siret, siren: r.siren, naf: r.naf, address: [r.address].filter(Boolean).join(" ") }));
    setResults([]);
    toast.success("Infos entreprise pré-remplies");
  };

  const createClient = async () => {
    if (!form.company_id) { toast.error("Sélectionnez une entreprise"); return; }
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      await api.post("/contacts", { ...form, company_ids: [form.company_id] });
      toast.success("Client créé"); setCreateOpen(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const onFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCSV(String(reader.result || ""));
      if (rows.length < 2) { toast.error("CSV vide ou invalide"); return; }
      const hdr = rows[0].map((h) => h.trim());
      setHeaders(hdr); setDataRows(rows.slice(1));
      // auto-map by header name
      const auto = {};
      FIELDS.forEach((f) => {
        const idx = hdr.findIndex((h) => h.toLowerCase().includes(f.k) || h.toLowerCase().includes(f.l.toLowerCase()));
        if (idx >= 0) auto[f.k] = String(idx);
      });
      setMapping(auto);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const preview = useMemo(() => {
    if (!dataRows.length) return [];
    return dataRows.map((r) => {
      const o = {};
      FIELDS.forEach((f) => { const idx = mapping[f.k]; if (idx !== undefined && idx !== "") o[f.k] = (r[Number(idx)] || "").trim(); });
      return o;
    }).filter((o) => o.name || o.email);
  }, [dataRows, mapping]);

  const runImport = async () => {
    if (!impCompany) { toast.error("Sélectionnez une entreprise"); return; }
    if (!preview.length) { toast.error("Aucune ligne exploitable (mappez au moins Nom ou Email)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/contacts/import", { company_id: impCompany, contacts: preview });
      toast.success(`${data.imported} client(s) importé(s)`); setImportOpen(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button data-testid="client-create-btn" onClick={openCreate} className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110"><UserPlus size={15} /> Nouveau client</button>
        <button data-testid="client-import-btn" onClick={openImport} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 text-sm font-medium hover:border-klein/40"><Upload size={15} /> Importer CSV</button>
      </div>

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><UserPlus size={18} className="text-klein" /> Nouveau client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="lbl">Entreprise (espace)</label>
              <select data-testid="create-client-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className="inp">
                {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* SIRET lookup / en création */}
            <div className="rounded-xl border border-klein/20 bg-klein/[0.05] p-3">
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input data-testid="create-client-encreation" type="checkbox" checked={form.en_creation} onChange={(e) => { setForm({ ...form, en_creation: e.target.checked }); setResults([]); }} className="w-4 h-4 accent-klein" />
                <span className="text-sm font-medium">Entreprise en création (saisie manuelle)</span>
              </label>
              {!form.en_creation ? (
                <>
                  <label className="lbl">SIRET ou nom de l'entreprise</label>
                  <div className="flex gap-2">
                    <input data-testid="create-client-siret" value={lookupQ} onChange={(e) => setLookupQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), doLookup())} className="inp flex-1" placeholder="Ex : 552032534 ou Danone" />
                    <button data-testid="create-client-lookup" onClick={doLookup} disabled={looking} className="h-[38px] px-3 rounded-lg bg-klein text-zinc-950 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">{looking ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Rechercher</button>
                  </div>
                  {results.length > 0 && (
                    <div data-testid="lookup-results" className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {results.map((r, i) => (
                        <button key={i} data-testid={`lookup-result-${i}`} onClick={() => pickCompany(r)} className="w-full text-left px-3 py-2 rounded-lg border border-white/10 hover:border-klein/50 bg-background flex items-start gap-2 transition-colors">
                          <Building2 size={14} className="text-klein mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{r.company_name} {r.state === "A" ? <span className="text-[10px] text-emerald-400">● active</span> : r.state === "C" ? <span className="text-[10px] text-red-400">● cessée</span> : null}</div>
                            <div className="text-[11px] text-zinc-500 truncate">SIRET {r.siret} · {r.address}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[12px] text-zinc-400">Aucun SIRET disponible : renseignez les informations ci-dessous manuellement.</p>
              )}
            </div>

            {/* Company info (auto-filled or manual) */}
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><label className="lbl">Société</label><input data-testid="create-client-companyname" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="inp" placeholder="Raison sociale" /></div>
              <div><label className="lbl">SIRET</label><input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="inp" /></div>
              <div><label className="lbl">Code NAF</label><input value={form.naf} onChange={(e) => setForm({ ...form, naf: e.target.value })} className="inp" /></div>
              <div className="col-span-2"><label className="lbl">Adresse</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp" /></div>
            </div>

            {/* Contact person */}
            <div className="border-t border-white/10 pt-3">
              <div className="text-[11px] uppercase tracking-wider text-klein font-semibold mb-2">Contact dans l'entreprise</div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="lbl">Nom *</label><input data-testid="create-client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" placeholder="Prénom Nom" /></div>
                <div><label className="lbl">Fonction</label><input data-testid="create-client-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="inp" placeholder="Ex : Gérant" /></div>
                <div><label className="lbl">Email</label><input data-testid="create-client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" placeholder="contact@…" /></div>
                <div><label className="lbl">Téléphone</label><input data-testid="create-client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" /></div>
              </div>
            </div>

            {/* Group */}
            <div><label className="lbl">Groupe de clients</label>
              {!newGroup ? (
                <select data-testid="create-client-group" value={form.group} onChange={(e) => { if (e.target.value === "__new__") { setNewGroup(true); setForm({ ...form, group: "" }); } else setForm({ ...form, group: e.target.value }); }} className="inp">
                  <option value="">— Aucun —</option>
                  {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                  <option value="__new__">➕ Nouveau groupe…</option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input data-testid="create-client-newgroup" autoFocus value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className="inp flex-1" placeholder="Nom du nouveau groupe" />
                  <button onClick={() => { setNewGroup(false); setForm({ ...form, group: "" }); }} className="h-[38px] px-3 rounded-lg border border-white/10 text-sm">Annuler</button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="h-9 px-4 rounded-lg border border-white/10 text-sm font-medium">Annuler</button>
            <button data-testid="create-client-submit" onClick={createClient} disabled={saving} className="h-9 px-4 rounded-lg bg-klein text-zinc-950 text-sm font-semibold disabled:opacity-50">Créer le client</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><FileSpreadsheet size={18} className="text-klein" /> Importer des clients (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="lbl">Entreprise cible</label>
              <select data-testid="import-company" value={impCompany} onChange={(e) => setImpCompany(e.target.value)} className="inp">
                {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {!headers.length ? (
              <label data-testid="import-dropzone" className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 rounded-xl py-8 cursor-pointer hover:border-klein/50 transition-colors">
                <Upload size={22} className="text-klein" />
                <span className="text-sm text-zinc-400">Choisir un fichier .csv</span>
                <span className="text-[11px] text-zinc-600">Séparateur , ou ; · 1re ligne = en-têtes</span>
                <input data-testid="import-file" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
              </label>
            ) : (
              <>
                <div className="text-xs text-zinc-500">Associez vos colonnes ({dataRows.length} lignes détectées) :</div>
                <div className="space-y-2">
                  {FIELDS.map((f) => (
                    <div key={f.k} className="flex items-center gap-2">
                      <span className="text-sm w-24 shrink-0">{f.l}{f.k === "name" ? " *" : ""}</span>
                      <ArrowRight size={13} className="text-zinc-500 shrink-0" />
                      <select data-testid={`map-${f.k}`} value={mapping[f.k] ?? ""} onChange={(e) => setMapping((m) => ({ ...m, [f.k]: e.target.value }))} className="inp flex-1">
                        <option value="">— Ignorer —</option>
                        {headers.map((h, i) => <option key={i} value={i}>{h || `Colonne ${i + 1}`}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div data-testid="import-preview-count" className="text-xs text-zinc-400 bg-background border border-white/10 rounded-lg px-3 py-2">{preview.length} client(s) prêt(s) à importer</div>
              </>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setImportOpen(false)} className="h-9 px-4 rounded-lg border border-white/10 text-sm font-medium">Annuler</button>
            {headers.length > 0 && <button data-testid="import-run" onClick={runImport} disabled={saving} className="h-9 px-4 rounded-lg bg-klein text-zinc-950 text-sm font-semibold disabled:opacity-50">Importer {preview.length}</button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <style>{`.lbl{font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:hsl(var(--muted-foreground));margin-bottom:4px;display:block}.inp{width:100%;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:8px;padding:8px 10px;font-size:14px;outline:none}`}</style>
    </>
  );
}
